import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { Box, ButtonBase, Divider, SxProps, Theme } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ReactNode, useMemo } from "react";
import {
  EXTERNAL_LINK_FEATURES,
  EXTERNAL_LINK_TARGET,
  HELP_CENTRE_BORDER_RADIUS,
  HELP_CENTRE_CARD_BACKGROUND_OPACITY,
  HELP_CENTRE_CARD_BORDER_OPACITY,
  HELP_CENTRE_CARD_TRANSITION,
  HELP_CENTRE_DISABLED_OPACITY,
  HELP_CENTRE_FONT_SIZE,
  HELP_CENTRE_FONT_WEIGHT,
  HELP_CENTRE_HEADING_ICON,
  HELP_CENTRE_HEADING_ICON_FONT_SIZE,
  HELP_CENTRE_HEADING_ICON_SIZE,
  HELP_CENTRE_LINKS,
  HELP_CENTRE_LINK_ARROW_ICON,
  HELP_CENTRE_SUBTITLE,
  HELP_CENTRE_SUPPORT_DETAILS,
  HELP_CENTRE_SUPPORT_TITLE,
  HELP_CENTRE_TITLE,
} from "../help-center-constant";
import {
  HelpCentreLink,
  HelpCentreSection,
  HelpCentreSupportDetail,
} from "../help-center-types";

export interface HelpCentreHomeProps {
  onSectionSelect: (section: HelpCentreSection) => void;
  sx?: SxProps<Theme>;
}

const HeadingIcon = HELP_CENTRE_HEADING_ICON;
const LinkArrowIcon = HELP_CENTRE_LINK_ARROW_ICON;

const HelpCentreHome = ({ onSectionSelect, sx }: HelpCentreHomeProps) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const linkCardSx: SxProps<Theme> = useMemo(
    () => ({
      width: "100%",
      p: 1.5,
      border: `1px solid ${alpha(primaryColor, HELP_CENTRE_CARD_BORDER_OPACITY)}`,
      backgroundColor: alpha(primaryColor, HELP_CENTRE_CARD_BACKGROUND_OPACITY),
      borderRadius: HELP_CENTRE_BORDER_RADIUS,
      transition: HELP_CENTRE_CARD_TRANSITION,
      "&:hover": { boxShadow: theme.shadows[1] },
    }),
    [primaryColor, theme.shadows]
  );

  const handleLinkClick = (link: HelpCentreLink) => {
    if (link.section) {
      onSectionSelect(link.section);
      return;
    }
    if (link.externalUrl) {
      window.open(
        link.externalUrl,
        EXTERNAL_LINK_TARGET,
        EXTERNAL_LINK_FEATURES
      );
    }
  };

  const renderSupportDetail = (detail: HelpCentreSupportDetail): ReactNode => {
    const DetailIcon = detail.icon;
    const { disabled } = detail;

    const detailContent = (
      <Flex
        alignItems="center"
        gap={1}
        sx={disabled ? { opacity: HELP_CENTRE_DISABLED_OPACITY } : undefined}
      >
        <DetailIcon
          fontSize="small"
          sx={{ color: disabled ? "action.disabled" : primaryColor }}
        />
        <Text
          size={HELP_CENTRE_FONT_SIZE.BODY}
          fontWeight={HELP_CENTRE_FONT_WEIGHT.MEDIUM}
          c={disabled ? "text.disabled" : undefined}
        >
          {detail.label}
        </Text>
      </Flex>
    );

    if (disabled) {
      return (
        <Box key={detail.key} aria-disabled>
          {detailContent}
        </Box>
      );
    }

    // if (detail.href) {
    //   const opensInNewTab = detail.href.startsWith(NEW_TAB_HREF_PREFIX);
    //   return (
    //     <Link
    //       key={detail.key}
    //       href={detail.href}
    //       underline="none"
    //       color="text.primary"
    //       target={opensInNewTab ? EXTERNAL_LINK_TARGET : undefined}
    //       rel={opensInNewTab ? EXTERNAL_LINK_FEATURES : undefined}
    //     >
    //       {detailContent}
    //     </Link>
    //   );
    // }

    if (!detail.section) {
      return <Box key={detail.key}>{detailContent}</Box>;
    }

    const { section } = detail;
    return (
      <ButtonBase
        key={detail.key}
        onClick={() => onSectionSelect(section)}
        sx={{
          justifyContent: "flex-start",
          borderRadius: HELP_CENTRE_BORDER_RADIUS,
        }}
      >
        {detailContent}
      </ButtonBase>
    );
  };

  return (
    <Flex flexDirection="column" gap={2} sx={sx}>
      <Flex alignItems="center" gap={1}>
        <Flex
          alignItems="center"
          justifyContent="center"
          sx={{
            width: HELP_CENTRE_HEADING_ICON_SIZE,
            height: HELP_CENTRE_HEADING_ICON_SIZE,
            borderRadius: HELP_CENTRE_BORDER_RADIUS,
            backgroundColor: primaryColor,
            color: theme.palette.primary.contrastText,
            flexShrink: 0,
          }}
        >
          <HeadingIcon sx={{ fontSize: HELP_CENTRE_HEADING_ICON_FONT_SIZE }} />
        </Flex>
        <Box>
          <Text
            size={HELP_CENTRE_FONT_SIZE.TITLE}
            fontWeight={HELP_CENTRE_FONT_WEIGHT.BOLD}
          >
            {HELP_CENTRE_TITLE}
          </Text>
          <Text size={HELP_CENTRE_FONT_SIZE.CAPTION} c="text.secondary">
            {HELP_CENTRE_SUBTITLE}
          </Text>
        </Box>
      </Flex>

      {HELP_CENTRE_LINKS.map((link) => {
        const LinkIcon = link.icon;
        return (
          <ButtonBase
            key={link.key}
            sx={linkCardSx}
            onClick={() => handleLinkClick(link)}
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              width="100%"
            >
              <Flex alignItems="center" gap={1}>
                <LinkIcon sx={{ color: primaryColor }} />
                <Box textAlign="left">
                  <Text
                    size={HELP_CENTRE_FONT_SIZE.BODY}
                    fontWeight={HELP_CENTRE_FONT_WEIGHT.SEMI_BOLD}
                    c={primaryColor}
                  >
                    {link.label}
                  </Text>
                  <Text size={HELP_CENTRE_FONT_SIZE.CAPTION} c="text.secondary">
                    {link.description}
                  </Text>
                </Box>
              </Flex>
              <LinkArrowIcon fontSize="small" sx={{ color: primaryColor }} />
            </Flex>
          </ButtonBase>
        );
      })}

      <Divider />
      <Text
        size={HELP_CENTRE_FONT_SIZE.CAPTION}
        fontWeight={HELP_CENTRE_FONT_WEIGHT.SEMI_BOLD}
        c="text.secondary"
        textTransform="uppercase"
      >
        {HELP_CENTRE_SUPPORT_TITLE}
      </Text>
      <Flex flexDirection="column" gap={2} mb={1} alignItems="flex-start">
        {HELP_CENTRE_SUPPORT_DETAILS.map(renderSupportDetail)}
      </Flex>
    </Flex>
  );
};

export default HelpCentreHome;
